import React, { createContext, PropsWithChildren, useEffect, useState } from "react";
import ContextMenu from "@/components/ContextMenu";
import { useWindowDimensions, LayoutChangeEvent, ViewStyle, StyleProp } from "react-native";
import { AnimatedStyle } from "react-native-reanimated";
import type { ContextMenuProviderProps } from "@/types/providers/contextMenu";

export const ContextMenuContext = createContext<ContextMenuProviderProps | undefined>(undefined);

export function ContextMenuProvider({ children }: PropsWithChildren) {
  const [visible, setVisible] = useState(false);
  const [dismiss, onDismiss] = useState<(args?: any) => void>();
  const [menuContent, setMenuContent] = useState<React.ReactNode>(<></>);
  const [style, setStyle] = useState<StyleProp<AnimatedStyle<StyleProp<ViewStyle>>>>(undefined);

  const [value, setValue] = useState("");
  const [ref, setIgnoreRef] = useState<React.RefObject<any> | undefined>();
  const [clickOutside, setClickOutside] = useState<React.SetStateAction<boolean>>(true);
  const [triggerElement, setTriggerElement] = useState<
    { x: number; y: number; width: number; height: number } | undefined
  >();
  const [actualMenuSize, setActualMenuSize] = useState<{ width: number; height: number } | undefined>();

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();

  useEffect(() => {
    if (!visible) {
      setMenuContent(<></>);
      setActualMenuSize(undefined);
    }
  }, [visible]);

  const [baseLocation, setBaseLocation] = useState<{ width: number; height: number; x: number; y: number }>({
    width: 200,
    height: 200,
    x: 10,
    y: 10,
  });

  // auto positioning if clipping
  const location = React.useMemo(() => {
    if (!triggerElement) return baseLocation;

    // Use actual menu size if available, otherwise fall back to baseLocation
    const menuWidth = actualMenuSize?.width ?? baseLocation.width;
    const menuHeight = actualMenuSize?.height ?? baseLocation.height;

    let adjustedX = baseLocation.x;
    let adjustedY = baseLocation.y;

    // Check if menu overflows right edge
    if (adjustedX + menuWidth > windowWidth) {
      adjustedX = windowWidth - menuWidth - 10; // 10px margin
    }

    // Check if menu overflows bottom edge - flip to show above trigger
    if (adjustedY + menuHeight > windowHeight) {
      adjustedY = triggerElement.y - menuHeight;
    }

    // Ensure menu doesn't go above screen
    if (adjustedY < 0) {
      adjustedY = 10; // 10px margin from top
    }

    return {
      ...baseLocation,
      x: adjustedX,
      y: adjustedY,
    };
  }, [baseLocation, triggerElement, actualMenuSize, windowWidth, windowHeight]);

  const handleMenuLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setActualMenuSize({ width, height });
  };

  // set back to default style after closing
  useEffect(() => {
    if (!visible) setStyle(undefined);
  }, [visible]);

  return (
    <ContextMenuContext.Provider
      value={{
        visible,
        setVisible,
        onDismiss,
        setIgnoreRef,
        setClickOutside,
        value,
        setValue,
        setStyle,
        setLocation: setBaseLocation,
        setMenuContent,
        setTriggerElement,
      }}
    >
      {children}
      <ContextMenu
        style={[{ width: location?.width, height: location?.height, left: location?.x, top: location?.y }, style]}
        visible={visible}
        setVisible={setVisible}
        ignoreRef={ref}
        clickOutside={clickOutside as boolean}
        onLayout={handleMenuLayout}
        onDismiss={() => {
          dismiss?.();
        }}
      >
        {menuContent}
      </ContextMenu>
    </ContextMenuContext.Provider>
  );
}
